import React, { useState, useRef } from 'react';
import { ChevronLeft, RotateCcw, Share, MoreHorizontal, X } from 'lucide-react';

interface InAppWebViewProps {
  url: string;
  onClose: () => void;
  title?: string;
}

const InAppWebView: React.FC<InAppWebViewProps> = ({ 
  url, 
  onClose, 
  title = "Web View" 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(url);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleReload = () => {
    setLoading(true);
    setError(false);
    if (iframeRef.current) {
      iframeRef.current.src = currentUrl;
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: currentUrl,
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(currentUrl);
        // You could show a toast notification here
        alert('Link copied to clipboard!');
      } catch (err) {
        console.log('Failed to copy to clipboard');
      }
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center">
          <button 
            onClick={onClose}
            className="mr-3 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {title}
            </h1>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleReload}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            disabled={loading}
          >
            <RotateCcw className={`w-5 h-5 text-gray-700 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          <button 
            onClick={handleShare}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <Share className="w-5 h-5 text-gray-700" />
          </button>
          
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {/* URL Bar */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center bg-white rounded-full px-3 py-2 border border-gray-300">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-3 flex-shrink-0"></div>
          <span className="text-sm text-gray-600 truncate">{currentUrl}</span>
        </div>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="absolute top-16 left-0 right-0 h-1 bg-gray-200 z-10">
          <div className="h-full bg-[#0DB5B5] animate-pulse"></div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 relative">
        {error ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="mb-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <MoreHorizontal className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Unable to load page
            </h3>
            <p className="text-gray-600 mb-4">
              Please check your internet connection and try again.
            </p>
            <button 
              onClick={handleReload}
              className="px-6 py-2 bg-[#0DB5B5] text-white rounded-lg font-medium hover:bg-[#0a9a9a] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={currentUrl}
            className="w-full h-full border-none"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title={title}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
          />
        )}
      </div>

      {/* Bottom Navigation (Optional) */}
      <div className="flex items-center justify-center py-2 bg-gray-50 border-t border-gray-200">
        <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
      </div>
    </div>
  );
};

export default InAppWebView;